import { Box, Card, Text, Stack, Table } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import { useElementSize } from '@mantine/hooks';
import { DateTime } from 'luxon';
import { AccountUtils } from '../../AccountUtils';
import type { MonthlyTransferData } from '../../hooks/useUsage';

interface TransferHistoryChartProps {
  monthlyTransfer: MonthlyTransferData[];
}

export function TransferHistoryChart({ monthlyTransfer }: TransferHistoryChartProps) {
  // Recharts' ResponsiveContainer needs a concrete pixel width from its parent.
  // Inside flex layouts (AppShell → Stack), width: 100% can resolve to 0 at mount.
  // Measuring the container first and deferring chart render avoids the -1 width error.
  const { ref: chartRef, width: chartWidth } = useElementSize();

  if (monthlyTransfer.length === 0) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Text c="dimmed" size="sm" ta="center" py="xl">
          No transfer history available
        </Text>
      </Card>
    );
  }

  const now = DateTime.now();
  const currentMonthStart = now.startOf('month').toSeconds();

  // Two series: 'history' (gray) and 'current' (blue). Each data point
  // populates exactly one series so current month is visually distinct.
  const chartData = monthlyTransfer.map(m => {
    const transferGB = Number(AccountUtils.bytesToGB(m.transferBytes).toFixed(2));
    const isCurrent = m.start >= currentMonthStart;
    return {
      month: DateTime.fromSeconds(m.start).toFormat("MMM ''yy"),
      history: isCurrent ? 0 : transferGB,
      current: isCurrent ? transferGB : 0,
    };
  });

  // Monthly totals for the table (sorted newest first)
  const monthlyTotals = [...monthlyTransfer]
    .sort((a, b) => b.start - a.start)
    .map(m => ({
      start: m.start,
      period: DateTime.fromSeconds(m.start).toFormat('MMMM yyyy'),
      transferGB: AccountUtils.formatBytesAsGB(m.transferBytes),
    }));

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Text fw={600} size="md">Monthly Data Transfer</Text>

        <Box ref={chartRef} w="100%">
          {chartWidth > 0 && (
            <BarChart
              h={300}
              data={chartData}
              dataKey="month"
              type="stacked"
              series={[
                { name: 'history', color: 'gray.4', label: 'Transfer' },
                { name: 'current', color: 'blue.6', label: 'Current Month' },
              ]}
              valueFormatter={(value) => `${value} GB`}
              tickLine="y"
              gridAxis="y"
            />
          )}
        </Box>

        <Table striped highlightOnHover aria-label="Monthly data transfer totals">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Period</Table.Th>
              <Table.Th ta="right">Transfer</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {monthlyTotals.map(row => (
              <Table.Tr key={row.start}>
                <Table.Td>
                  <Text size="sm">{row.period}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">{row.transferGB}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
